<script setup lang="ts">
import { ref, computed, watch } from "vue";
import qs from "qs";
import { getSeoObject } from "~/helpers/seo";
import { EducationSmallCard, EducationBigCard } from "#components";

// Reactive property for storing fetched data
const mainData = ref(null);

function buildQuery() {
  return qs.stringify(
    {
      fields: ["title", "description"],
      populate: {
        seo: "*",
        hero_posts: {
          fields: ["main_button_text"],
          populate: {
            main_post: {
              fields: ["slug", "title", "subTitle", "image"],
              populate: {
                image: {
                  fields: ["url", "name"],
                },
              },
            },
            secondary_posts: {
              fields: ["slug", "title", "image"],
              populate: {
                image: {
                  fields: ["url", "name"],
                },
              },
            },
          },
        },
        page_menu: {
          fields: ["all_levels_text"],
          populate: {
            categories: {
              fields: ["name"],
            },
          },
        },
        sections: {
          fields: ["title", "link_text", "education_category"],
          populate: {
            education_category: {
              fields: ["slug"],
            },
          },
        },
      },
      sort: "createdAt:desc",
    },
    { encodeValuesOnly: true },
  );
}

// Fetch data based on the current language
async function fetchData() {
  const query = buildQuery();
  const { data: fetchedData } = await useCustomFetch(
    `/api/strapi-full/education-center?${query}`,
  );
  mainData.value = fetchedData.value?.data?.attributes;
}

// Fetch data on initial load
fetchData();

// Computed properties for accessing data attributes
const data = computed(() => mainData.value);
const mainPost = computed(() => data.value?.hero_posts?.main_post?.data);
const otherPosts = computed(
  () => data.value?.hero_posts?.secondary_posts?.data,
);

// Watch for language changes
watch(
  () => $i18n.locale, // Adjust this to your language setting property
  () => {
    fetchData(); // Refetch data when the language changes
  },
);
</script>

<template>
  <section v-if="data" class="main-container">
    <SeoMeta v-if="data?.seo" v-bind="data?.seo" />
    <div class="container">
      <div class="row">
        <div class="col-md-12">
          <h2 class="page-title">{{ data?.title }}</h2>
          <p class="text-center" style="white-space: pre-line">
            {{ data?.description }}
          </p>
        </div>
      </div>

      <div
        v-if="data.page_menu.categories.data"
        class="filters category-section mb-30"
      >
        <ul class="nav nav-pills nav-fill nav-filters">
          <li class="nav-item">
            <a class="nav-link active" aria-current="page" href="#all-levels">{{
              data.page_menu?.all_levels_text
            }}</a>
          </li>
          <li
            v-for="category in data?.page_menu?.categories?.data"
            class="nav-item"
          >
            <a
              class="nav-link"
              :href="`#${category?.attributes?.name?.toLowerCase()}`"
            >
              {{ category?.attributes?.name }}
            </a>
          </li>
        </ul>
      </div>

      <div id="all-levels" class="education-box-card br-30">
        <div class="container">
          <div class="row">
            <div class="col-xs-12 col-lg-6">
              <EducationBigCard
                v-if="mainPost"
                :slug="mainPost?.attributes?.slug"
                :title="mainPost?.attributes?.title"
                :sub-title="mainPost?.attributes?.subTitle"
                :image="mainPost?.attributes?.image"
                :button-text="data?.hero_posts?.main_button_text"
              />
            </div>

            <div class="col-xs-12 col-lg-6">
              <EducationSmallCard
                v-for="post in otherPosts"
                :key="post?.id"
                :slug="post?.attributes?.slug"
                :title="post?.attributes?.title"
                :image="post?.attributes?.image"
              />
            </div>
          </div>
        </div>
      </div>

      <EducationCategorySection
        v-if="data?.sections"
        v-for="section in data?.sections"
        :section="section"
        :category-id="section?.education_category?.data?.id"
        :slug="section?.education_category?.data?.attributes?.slug"
      />
    </div>
  </section>

  <JoinCta />
</template>
